/**
 * File: packagesData.ts
 * Path: packages/config/src/pricing/packagesData.ts
 * Description: Core growth packages and innovation tiers. Packages are mapped as direct
 * extensions of their foundational services (inheriting category/timeline) while overriding
 * specific bundle data.
 */
import { InnovationTier, Package, PackageId } from "./pricingTypes";
import { SERVICES } from "./servicesData";
import type { CarePlanId } from "./pricingTypes";
import { CARE_PLANS } from "./careData";
import {
  FORGE_PAYMENT_OPTIONS,
  INNOVATION_PRICING,
  PACKAGE_TERM_MONTHS,
  PAYMENT_REGISTRY,
  STANDARD_PAYMENT_OPTIONS,
  VENTURE_PAYMENT_OPTIONS,
} from "./paymentConstants";

// ==========================================
// 1. SERVICE TIER MAP (SSOT)
// ==========================================

/**
 * Each package holds one main build plus what goes with it. No package holds
 * two websites: the booking site replaces the 5-page site, and the app
 * replaces both. Brand Look is an add-on.
 */
const TIER_INCLUSIONS = {
  spark: [
    SERVICES.starterWebsite.id,
    SERVICES.localSearch.id,
    SERVICES.professionalEmail.id,
  ],
  ignite: [
    SERVICES.unlimitedWebsite.id,
    SERVICES.localSearch.id,
    SERVICES.professionalEmail.id,
    SERVICES.activeMarketing.id,
    SERVICES.performanceSSR.id,
    SERVICES.strategyAudit.id,
  ],
  blaze: [
    SERVICES.techHub.id,
    SERVICES.localSearch.id,
    SERVICES.professionalEmail.id,
    SERVICES.advancedGrowth.id,
    SERVICES.businessTracking.id,
  ],
} as const satisfies Record<PackageId, readonly string[]>;

/** Bundle prices, about 15% under buying the builds one by one. */
const BUNDLE_PRICES: Record<PackageId, number> = {
  spark: 8500,
  ignite: 26500,
  blaze: 38000,
};

const PACKAGE_CARE: Record<PackageId, CarePlanId> = {
  spark: "site",
  ignite: "booking",
  blaze: "app",
};

/**
 * The monthly price: the bundle at the 12-month plan's total, spread over the
 * term, plus care, rounded up to the next R50.
 */
export const packageMonthly = (bundle: number, care: number): number => {
  const total = bundle * (1 + PAYMENT_REGISTRY.split_12_month.markup);
  return Math.ceil((total / PACKAGE_TERM_MONTHS + care) / 50) * 50;
};

const packagePricing = (id: PackageId) => {
  const care = CARE_PLANS[PACKAGE_CARE[id]];
  return {
    investment: {
      amount: packageMonthly(BUNDLE_PRICES[id], care.amount),
      structure: "monthly",
      term: `${PACKAGE_TERM_MONTHS} months, then ${care.name} only`,
    },
    bundlePrice: BUNDLE_PRICES[id],
    carePlanId: care.id,
    includedServiceIds: TIER_INCLUSIONS[id],
  } as const;
};

// ==========================================
// 2. PACKAGE REGISTRY
// ==========================================

export const PACKAGES: Record<PackageId, Package> = {
  spark: {
    // Inherit base service attributes (category: "web", timeline: "14 days")
    ...SERVICES.starterWebsite,

    // Override package-specific details
    id: "spark",
    name: "SPARK",
    type: "package",
    tagline:
      "Your website, your Google listing and your business email, sorted.",
    ...packagePricing("spark"),
    paymentOptions: STANDARD_PAYMENT_OPTIONS,
    features: [
      SERVICES.starterWebsite.features[0],
      SERVICES.localSearch.features[0],
      {
        title: "Site Care",
        description:
          "We handle the technical background. Fast, secure, and backed up daily.",
      },
    ],
    tags: ["visibility", "core", "budget"],
  },
  ignite: {
    // Inherit base service attributes (category: "web", timeline: "21-30 days")
    ...SERVICES.unlimitedWebsite,

    // Override package-specific details
    id: "ignite",
    name: "IGNITE",
    type: "package",
    tagline: "Bookings, orders, and marketing that sell for you automatically.",
    ...packagePricing("ignite"),
    paymentOptions: STANDARD_PAYMENT_OPTIONS,
    features: [
      SERVICES.unlimitedWebsite.features[0],
      SERVICES.activeMarketing.features[0],
      SERVICES.performanceSSR.features[0],
      SERVICES.strategyAudit.features[0],
      {
        title: "Already a client? What you paid counts",
        description:
          "If you already bought a website from us, what you paid for it comes off this package.",
        deliverable: "Credit for your earlier website",
      },
    ],
    tags: ["transact", "loyalty", "automation", "growth"],
  },
  blaze: {
    // Inherit base service attributes (category: "app", timeline: "30-45 days")
    ...SERVICES.techHub,

    // Override package-specific details
    id: "blaze",
    name: "BLAZE",
    type: "package",
    tagline: "Your own app, growth marketing, and a team on call to scale it.",
    ...packagePricing("blaze"),
    paymentOptions: STANDARD_PAYMENT_OPTIONS,
    features: [
      SERVICES.techHub.features[0],
      SERVICES.advancedGrowth.features[0],
      SERVICES.businessTracking.features[0],
      {
        title: "Your Digital Partner on Call",
        description:
          "Direct line to our experts. We work with you to plan and execute long-term growth.",
        deliverable: "Monthly Growth Meetings",
      },
      {
        title: "Already a client? What you paid counts",
        description:
          "If you already bought a website from us, what you paid for it comes off this package.",
        deliverable: "Credit for your earlier website",
      },
    ],
    tags: ["operate", "scale", "app", "growth"],
  },
};

// ==========================================
// 3. INNOVATION REGISTRY
// ==========================================

export const INNOVATIONS = {
  forge: {
    id: "forge",
    name: "FORGE",
    type: "innovation",
    subtitle: "Custom Computer Tools Built from Scratch",
    tagline: "If it doesn't exist yet, we build it for you.",
    investment: {
      amount: 11500,
      structure: "per-sprint",
      term: "Fixed stages, about two weeks each",
    },
    paymentOptions: FORGE_PAYMENT_OPTIONS,
    features: [
      SERVICES.masterPlan.features[0],
      {
        title: "Fast, Step-by-Step Building",
        description:
          "Built in fixed stages of about two weeks each, to a fixed price set by your Blueprint. See it coming to life as we go. The per-stage figure is an estimate until the Blueprint fixes the price.",
        deliverable: "A check-in at the end of every stage",
      },
      {
        title: "Built to Grow",
        description: "Modern code that won't crash under high customer load.",
      },
      {
        title: "Yours to Run",
        description:
          "Once it's paid for, the finished system is yours to use for good. We keep the source code.",
      },
    ],
    tags: ["operate", "complex", "custom", "software"],
  },
  venture: {
    id: "venture",
    name: "VENTURE PROTOCOL",
    type: "innovation",
    subtitle: "We Build It Together",
    tagline: "Your dedicated digital business partner.",
    investment: {
      amount: INNOVATION_PRICING.venture.retainer_investment,
      structure: "retainer",
      term: "Partnership Retainer",
    },
    equityRange: "10-20%",
    paymentOptions: VENTURE_PAYMENT_OPTIONS,
    features: [
      {
        title: "Your Digital Partner",
        description: "We handle the tech while you focus on making sales.",
      },
      {
        title: "Fast First Version",
        description:
          "Quickly launch a working version to test with real customers.",
      },
    ],
    tags: ["partnership", "startup", "growth"],
  },
} as const satisfies Record<string, InnovationTier>;
